/**
 * GEX (Gamma Exposure) Calculator — ported from Pepsi project (src/processor.js).
 * Computes 4 key gamma levels from option chain rows:
 *   Gamma Flip, Zero Gamma, Call Wall, Put Wall.
 *
 * Supports DTE-aware grouping: aggregate by expiration bucket.
 * Uses same formulas as gammaCalculations.ts and Pepsi backend.
 */

const CONTRACT_SIZE = 100;
const DEFAULT_IV = 0.2;
const DEFAULT_T = 30 / 365.25;

// ── Types ─────────────────────────────────────────────────────────────────

export interface OptionRow {
    Strike?: number | string;
    strike?: number | string;
    "Strike Price"?: number | string;
    "Option Type"?: string;
    optionType?: string;
    "Open Interest"?: number | string;
    OpenInterest?: number | string;
    open_interest?: number | string;
    Volume?: number | string;
    volume?: number | string;
    Vol?: number | string;
    Gamma?: number | string;
    gamma?: number | string;
    "Underlying Price"?: number | string;
    UnderlyingPrice?: number | string;
    underlying_price?: number | string;
    spot?: number;
    "Implied Volatility"?: number | string;
    iv?: number | string;
    implied_volatility?: number | string;
    "Expiration Date"?: string;
    Expiration?: string;
    expiry?: string;
    expirationDate?: string;
    expiration?: string;
    "Expiry Date"?: string;
    [key: string]: any;
}

export interface StrikeAgg {
    strike: number;
    callOI: number;
    putOI: number;
    callVol: number;
    putVol: number;
    netSpotGamma: number;
    netGex: number;
    /** Same exposure measured against today's traded volume instead of open interest. */
    netVolSpotGamma: number;
    netVolGex: number;
}

/** Levels derived from one exposure series; computed identically for OI and for volume. */
export interface GexLevelSet {
    gammaFlip: number;
    zeroGamma: number | null;
    callWall: number;
    putWall: number;
    /** Size behind each wall — open interest for the OI set, contracts traded for the volume set. */
    callWallSize: number;
    putWallSize: number;
    /** Signed sum across strikes. Positive = dealers long gamma (dampening). */
    netGex: number;
}

export interface GexLevels {
    spot: number;
    gammaFlip: number;
    zeroGamma: number | null;
    callWall: number;
    putWall: number;
    callWallOI: number;
    putWallOI: number;
    strikes: StrikeAgg[];
    top5ByOI: StrikeAgg[];
    top5ByVol: StrikeAgg[];
    /** Net GEX across all strikes, from open interest. */
    netGex: number;
    /**
     * The same levels computed from today's volume. Open interest is the book as it stood at
     * yesterday's close; volume is what is actually trading now, so the two disagree exactly when
     * positioning is shifting — which is the interesting case.
     */
    volume: GexLevelSet;
    /** Contracts that passed the volume band, and how many were excluded by it. */
    contractsUsed: number;
    contractsFiltered: number;
    /** Observed per-contract traded volume among contracts that traded, for sizing the filters. */
    volRange: { min: number; max: number };
}

/** Restrict which contracts count, by the volume they have traded today. */
export interface GexAggOptions {
    /** Drop contracts trading below this many lots — a noise floor for thin strikes. */
    minVolume?: number;
    /** Drop contracts trading above this many lots — caps a single outlier strike. */
    maxVolume?: number;
}

function passesVolumeBand(vol: number, opts?: GexAggOptions): boolean {
    if (!opts) return true;
    if (opts.minVolume != null && opts.minVolume > 0 && vol < opts.minVolume) return false;
    if (opts.maxVolume != null && opts.maxVolume > 0 && vol > opts.maxVolume) return false;
    return true;
}

export interface GexGroup {
    dte: number | string;
    count: number;
    levels: GexLevels;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function safeNum(v: unknown): number {
    if (v == null || v === "" || v === "-") return 0;
    const n =
        typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
}

function isCall(row: OptionRow): boolean {
    const t = String(row["Option Type"] ?? row.optionType ?? "").toUpperCase();
    return t === "C" || t === "CALL";
}

function parseExpiry(raw: unknown): Date | null {
    if (raw == null || raw === "") return null;
    if (typeof raw === "number") {
        const ms = raw < 1e12 ? raw * 1000 : raw;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
    }
    const s = String(raw).trim();
    // ISO: 2025-12-19
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    // US: MM/DD/YYYY
    const m2 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (m2) {
        let y = +m2[3];
        if (y < 100) y += 2000;
        return new Date(Date.UTC(y, +m2[1] - 1, +m2[2]));
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
}

function dteFor(row: OptionRow, todayUtc: number): number {
    const raw =
        row["Expiration Date"] ??
        row["Expiration"] ??
        row.expiry ??
        row.expirationDate ??
        row.expiration ??
        row["Expiry Date"];
    const d = parseExpiry(raw);
    if (!d) return -1; // unknown DTE
    const days = Math.round((d.getTime() - todayUtc) / 86400000);
    return Math.max(days, 0);
}

function bsGamma(S: number, K: number, T: number, sigma: number): number {
    if (S <= 0 || K <= 0 || T <= 0 || sigma <= 0) return 0;
    const sqrtT = Math.sqrt(T);
    const d1 =
        (Math.log(S / K) + (0.05 + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
    const nD1 = Math.exp(-0.5 * d1 * d1) / Math.sqrt(2 * Math.PI);
    return nD1 / (S * sigma * sqrtT);
}

function getGamma(row: OptionRow, S: number): number {
    const g = safeNum(row["Gamma"] ?? row.Gamma ?? row.gamma);
    if (g > 1e-12) return g;
    const K = safeNum(row["Strike"] ?? row.strike ?? row["Strike Price"]);
    if (K <= 0 || S <= 0) return 0;
    const ivRaw = row["Implied Volatility"] ?? row.iv ?? row.implied_volatility;
    let sigma = DEFAULT_IV;
    if (ivRaw != null && ivRaw !== "" && ivRaw !== "-") {
        const iv = safeNum(ivRaw);
        if (iv > 0) sigma = iv < 2 ? iv : iv / 100;
    }
    return bsGamma(S, K, DEFAULT_T, sigma);
}

function getUnderlyingPrice(rows: OptionRow[]): number {
    for (const row of rows.slice(0, 10)) {
        const v = safeNum(
            row["Underlying Price"] ??
                row.UnderlyingPrice ??
                row.underlying_price ??
                row.spot,
        );
        if (v > 0) return v;
    }
    return 0;
}

// ── Core Aggregation ───────────────────────────────────────────────────────

/**
 * Derive the four levels from one exposure series. Both the open-interest and the volume view use
 * this, so the two are always computed the same way and stay directly comparable.
 */
function deriveLevels(
    strikes: StrikeAgg[],
    spotGammaOf: (s: StrikeAgg) => number,
    gexOf: (s: StrikeAgg) => number,
    callSizeOf: (s: StrikeAgg) => number,
    putSizeOf: (s: StrikeAgg) => number,
): GexLevelSet {
    // Zero Gamma: where cumulative GEX crosses zero.
    let cum = 0;
    let zeroGamma: number | null = null;
    let netGex = 0;
    for (const s of strikes) {
        const prev = cum;
        cum += gexOf(s);
        netGex = cum;
        if (
            zeroGamma === null &&
            ((prev < 0 && cum >= 0) || (prev > 0 && cum <= 0))
        ) {
            zeroGamma = s.strike;
        }
    }

    // Gamma Flip: strike carrying the most absolute spot gamma.
    const peak = strikes.reduce(
        (m, s) =>
            Math.abs(spotGammaOf(s)) > Math.abs(spotGammaOf(m)) ? s : m,
        strikes[0],
    );
    const callWall = strikes.reduce(
        (m, s) => (callSizeOf(s) > callSizeOf(m) ? s : m),
        strikes[0],
    );
    const putWall = strikes.reduce(
        (m, s) => (putSizeOf(s) > putSizeOf(m) ? s : m),
        strikes[0],
    );

    return {
        gammaFlip: peak.strike,
        zeroGamma,
        callWall: callWall.strike,
        putWall: putWall.strike,
        callWallSize: callSizeOf(callWall),
        putWallSize: putSizeOf(putWall),
        netGex,
    };
}

function aggregate(
    rows: OptionRow[],
    spot: number,
    opts?: GexAggOptions,
): GexLevels | null {
    if (!rows.length) return null;
    const byStrike = new Map<number, StrikeAgg>();
    let contractsUsed = 0;
    let contractsFiltered = 0;
    let volMin = Infinity;
    let volMax = 0;

    for (const row of rows) {
        const strike = safeNum(
            row["Strike"] ?? row.strike ?? row["Strike Price"],
        );
        if (!strike) continue;

        const vol = safeNum(row["Volume"] ?? row.volume ?? row.Vol ?? row.vol);
        // Observed range is taken before filtering, so the UI can suggest a sensible band.
        if (vol > 0) {
            volMin = Math.min(volMin, vol);
            volMax = Math.max(volMax, vol);
        }
        if (!passesVolumeBand(vol, opts)) {
            contractsFiltered += 1;
            continue;
        }
        contractsUsed += 1;

        const S =
            spot ||
            safeNum(row["Underlying Price"] ?? row.underlying_price) ||
            100;
        const oi = safeNum(
            row["Open Interest"] ?? row.OpenInterest ?? row.open_interest,
        );
        const gamma = getGamma(row, S);
        const sign = isCall(row) ? 1 : -1;
        const call = isCall(row);

        // Spot gamma = Gamma × OI × S² × 0.01 × sign (matches Pepsi/Polygon backend)
        const spotGamma = gamma * oi * S * S * 0.01 * sign;
        // GEX = Gamma × OI × 100 × S × sign × 0.01
        const gex = gamma * oi * CONTRACT_SIZE * S * sign * 0.01;
        // Identical formulas against volume: the exposure implied by what traded today.
        const volSpotGamma = gamma * vol * S * S * 0.01 * sign;
        const volGex = gamma * vol * CONTRACT_SIZE * S * sign * 0.01;

        if (!byStrike.has(strike)) {
            byStrike.set(strike, {
                strike,
                callOI: 0,
                putOI: 0,
                callVol: 0,
                putVol: 0,
                netSpotGamma: 0,
                netGex: 0,
                netVolSpotGamma: 0,
                netVolGex: 0,
            });
        }
        const agg = byStrike.get(strike)!;
        agg.netSpotGamma += spotGamma;
        agg.netGex += gex;
        agg.netVolSpotGamma += volSpotGamma;
        agg.netVolGex += volGex;
        if (call) {
            agg.callOI += oi;
            agg.callVol += vol;
        } else {
            agg.putOI += oi;
            agg.putVol += vol;
        }
    }

    const strikes = Array.from(byStrike.values()).sort(
        (a, b) => a.strike - b.strike,
    );
    if (!strikes.length) return null;

    const oiSet = deriveLevels(
        strikes,
        (s) => s.netSpotGamma,
        (s) => s.netGex,
        (s) => s.callOI,
        (s) => s.putOI,
    );
    const volSet = deriveLevels(
        strikes,
        (s) => s.netVolSpotGamma,
        (s) => s.netVolGex,
        (s) => s.callVol,
        (s) => s.putVol,
    );

    return {
        spot,
        strikes,
        gammaFlip: oiSet.gammaFlip,
        zeroGamma: oiSet.zeroGamma,
        callWall: oiSet.callWall,
        putWall: oiSet.putWall,
        callWallOI: oiSet.callWallSize,
        putWallOI: oiSet.putWallSize,
        netGex: oiSet.netGex,
        volume: volSet,
        contractsUsed,
        contractsFiltered,
        volRange: {
            min: volMin === Infinity ? 0 : volMin,
            max: volMax,
        },
        top5ByOI: [...strikes]
            .sort((a, b) => b.callOI + b.putOI - (a.callOI + a.putOI))
            .slice(0, 5),
        top5ByVol: [...strikes]
            .sort((a, b) => b.callVol + b.putVol - (a.callVol + a.putVol))
            .slice(0, 5),
    };
}

// ── Public API ─────────────────────────────────────────────────────────────

/** Aggregate ALL rows into a single GEX level group. */
export function calculateGexLevels(
    rows: OptionRow[],
    opts?: GexAggOptions,
): GexLevels | null {
    if (!rows || !rows.length) return null;
    const spot = getUnderlyingPrice(rows);
    if (spot <= 0)
        console.warn(
            "[gexCalc] Could not determine underlying price from data",
        );
    return aggregate(rows, spot, opts);
}

/** Group rows by DTE, aggregate each group separately. */
export function calculateGexByDte(
    rows: OptionRow[],
    dteFilter: (number | string)[] = ["all"],
    opts?: GexAggOptions,
): GexGroup[] {
    if (!rows || !rows.length) return [];
    const spot = getUnderlyingPrice(rows);
    const now = new Date();
    const todayUtc = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
    );

    // Index rows by DTE
    const byDte = new Map<number, OptionRow[]>();
    for (const row of rows) {
        const dte = dteFor(row, todayUtc);
        if (dte < 0) continue; // skip unknown DTEs
        if (!byDte.has(dte)) byDte.set(dte, []);
        byDte.get(dte)!.push(row);
    }

    const out: GexGroup[] = [];
    for (const wanted of dteFilter) {
        if (wanted === "all") {
            const lv = aggregate(rows, spot, opts);
            if (lv) out.push({ dte: "all", count: rows.length, levels: lv });
            continue;
        }
        const subset = byDte.get(Number(wanted)) || [];
        if (!subset.length) continue;
        const lv = aggregate(subset, spot, opts);
        if (lv)
            out.push({ dte: Number(wanted), count: subset.length, levels: lv });
    }
    return out;
}

/** Discover which DTE values actually exist in the rows. */
export function discoverDtes(rows: OptionRow[]): number[] {
    if (!rows || !rows.length) return [];
    const now = new Date();
    const todayUtc = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
    );
    const dtes = new Set<number>();
    for (const row of rows) {
        const dte = dteFor(row, todayUtc);
        if (dte >= 0) dtes.add(dte);
    }
    return Array.from(dtes).sort((a, b) => a - b);
}

/**
 * Futures scaling: if viewing ES/NQ/GC etc, scale GEX levels to the futures price.
 * Example: ES at 5920, SPX spot at 5900 → ratio = 1.00339
 */
export function scaleGexForFutures(
    groups: GexGroup[],
    futuresPrice: number,
): GexGroup[] {
    if (!futuresPrice || futuresPrice <= 0) return groups;
    const underlyingSpot = groups[0]?.levels?.spot;
    if (!underlyingSpot || underlyingSpot <= 0) return groups;

    const ratio = futuresPrice / underlyingSpot;
    if (Math.abs(ratio - 1) < 0.001) return groups;

    return groups.map((g) => {
        const lv = { ...g.levels };
        lv.spot = futuresPrice;
        if (lv.gammaFlip)
            lv.gammaFlip = Math.round(lv.gammaFlip * ratio * 100) / 100;
        if (lv.zeroGamma)
            lv.zeroGamma = Math.round(lv.zeroGamma * ratio * 100) / 100;
        if (lv.callWall)
            lv.callWall = Math.round(lv.callWall * ratio * 100) / 100;
        if (lv.putWall) lv.putWall = Math.round(lv.putWall * ratio * 100) / 100;
        // The volume levels are strikes on the same underlying, so they scale identically —
        // leaving them unscaled would draw them against a futures price they do not belong to.
        if (lv.volume) {
            const v = { ...lv.volume };
            const px = (n: number) => Math.round(n * ratio * 100) / 100;
            if (v.gammaFlip) v.gammaFlip = px(v.gammaFlip);
            if (v.zeroGamma) v.zeroGamma = px(v.zeroGamma);
            if (v.callWall) v.callWall = px(v.callWall);
            if (v.putWall) v.putWall = px(v.putWall);
            lv.volume = v;
        }
        return { ...g, levels: lv };
    });
}
